import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Text,
  Card,
  SkeletonLoader,
  Tooltip,
  Typeahead,
  AdminAPICategory,
  APICategory,
  UnreleasedAPICategory,
  displayNameForAPICategory,
} from "@merge-api/merge-javascript-shared";
import { useHistory, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import queryString from "query-string";
import { fetchWithAuth } from "../../api-client/api_client";
import MergeTable from "../shared/MergeTable";
import { IntegrationAvatar } from "../shared/MergeAvatars";
import {
  CODE_BLOCK_PROGRESS_PATH,
  getCodeBlockProgressIntegrationPath,
} from "../../router/RouterUtils";
import {
  AggregateMetrics,
  AggregateStatCards,
  ErrorBanner,
  barClassForCategory,
  useSortState,
  SortTh,
} from "./codeBlockProgressShared";

// ─── Types ────────────────────────────────────────────────────────────────────

interface IntegrationProgress extends AggregateMetrics {
  id: string;
  name: string;
  slug: string;
  image: string | null;
}

interface CategoryResult extends AggregateMetrics {
  category: string;
  integrations: IntegrationProgress[];
}

interface CategorySummary {
  data: CategoryResult | null;
  loading: boolean;
  error: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TRACKED_CATEGORIES: AdminAPICategory[] = [
  APICategory.hris,
  APICategory.ats,
  APICategory.filestorage,
  APICategory.knowledgebase,
  UnreleasedAPICategory.calendar,
  APICategory.chat,
  UnreleasedAPICategory.email,
  APICategory.ticketing,
  APICategory.crm,
  APICategory.accounting,
];

// ─── Category card ─────────────────────────────────────────────────────────────

const CategoryCard = ({
  category,
  summary,
  onClick,
}: {
  category: AdminAPICategory;
  summary: CategorySummary;
  onClick: () => void;
}) => {
  const { data, loading, error } = summary;

  const totalIntegrations =
    data?.integrations.filter((i) => i.total_fetch_blueprints > 0).length ?? 0;
  const fullyConvertedIntegrations =
    data?.integrations.filter(
      (i) => i.total_fetch_blueprints > 0 && i.blueprints_fully_converted > 0
    ).length ?? 0;
  const pct = totalIntegrations > 0 ? (fullyConvertedIntegrations / totalIntegrations) * 100 : 0;

  return (
    <div
      className="px-7 py-6 cursor-pointer rounded-lg border border-gray-20 hover:border-gray-30 transition-colors flex flex-col gap-5 bg-white"
      onClick={onClick}
    >
      <Text variant="h4" className="text-gray-70 leading-none">
        {displayNameForAPICategory(category)}
      </Text>

      {loading && (
        <div className="flex flex-col gap-2 mt-1">
          <SkeletonLoader height={10} width={240} borderRadius={4} />
          <SkeletonLoader height={12} width={120} borderRadius={4} />
        </div>
      )}

      {error && (
        <Text variant="sm" className="text-red-50">
          Failed to load
        </Text>
      )}

      {data && (
        <>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Text variant="sm" className="text-gray-40">
                {fullyConvertedIntegrations} / {totalIntegrations} integrations
              </Text>
              <span className="text-sm font-semibold text-gray-60 tabular-nums">
                {pct.toFixed(1)}%
              </span>
            </div>
            <div className="h-3 bg-gray-20 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${barClassForCategory(category)}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const CodeBlockProgressPage = () => {
  const history = useHistory();
  const location = useLocation();

  const selectedCategory =
    (queryString.parse(location.search).category as AdminAPICategory) ?? null;

  const [summaries, setSummaries] = useState<Record<string, CategorySummary>>(
    Object.fromEntries(
      TRACKED_CATEGORIES.map((c) => [c, { data: null, loading: true, error: false }])
    )
  );

  useEffect(() => {
    TRACKED_CATEGORIES.forEach((category) => {
      fetchWithAuth({
        path: `/integrations/code-block-conversion-progress?category=${category}`,
        method: "GET",
        onResponse: (data: CategoryResult) => {
          setSummaries((prev) => ({ ...prev, [category]: { data, loading: false, error: false } }));
        },
        onError: () => {
          setSummaries((prev) => ({
            ...prev,
            [category]: { data: null, loading: false, error: true },
          }));
        },
      });
    });
  }, []);

  const handleCategoryClick = (cat: AdminAPICategory) => {
    history.push(`${CODE_BLOCK_PROGRESS_PATH}?category=${cat}`);
  };

  type IntegrationSortCol =
    | "name"
    | "total_fetch_blueprints"
    | "blueprints_with_at_least_one_code_block"
    | "blueprints_fully_converted"
    | "blueprints_with_code_block_and_no_model_writes"
    | "code_block_step_ratio"
    | "code_block_step_ratio_supported_steps_only";

  const { sortCol: iSortCol, sortDir: iSortDir, handleSort: iHandleSort } = useSortState<
    IntegrationSortCol
  >("blueprints_fully_converted");

  const summary = selectedCategory ? summaries[selectedCategory] : null;

  const sortedIntegrations = summary?.data
    ? [...summary.data.integrations].sort((a, b) => {
        const cmp = iSortCol === "name" ? a.name.localeCompare(b.name) : a[iSortCol] - b[iSortCol];
        return iSortDir === "asc" ? cmp : -cmp;
      })
    : [];

  // ── Category detail view ───────────────────────────────────────────────────

  if (selectedCategory) {
    return (
      <Container fluid className="pb-8 px-8 pt-16">
        <Link
          to={CODE_BLOCK_PROGRESS_PATH}
          className="inline-flex items-center gap-1 text-sm text-gray-40 hover:text-gray-70 mt-6 mb-3"
        >
          <ArrowLeft size={14} />
          All categories
        </Link>

        <div className="flex items-center justify-between mb-4">
          <Text variant="h2" className="text-gray-70">
            {displayNameForAPICategory(selectedCategory)}
          </Text>
          <div className="w-48">
            <Typeahead
              options={TRACKED_CATEGORIES}
              getOptionLabel={(cat: AdminAPICategory) => displayNameForAPICategory(cat)}
              value={selectedCategory}
              onChange={(_event, value) => {
                if (value) history.push(`${CODE_BLOCK_PROGRESS_PATH}?category=${value}`);
              }}
              disableClearable
              placeholder="Switch category…"
            />
          </div>
        </div>
        <div className="mb-4 bg-gray-20 h-px" />

        <div className="mb-6 inline-flex items-center gap-2 rounded-md bg-blue-0 border border-blue-20 px-3 py-2 text-sm text-blue-60">
          <Info size={14} className="shrink-0" />
          Metrics cover fetch blueprints only.
        </div>

        {summary?.error && <ErrorBanner message="Failed to load category data." />}

        {summary?.loading && (
          <>
            {/* Skeleton stat cards */}
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
              {[...Array(3)].map((_, i) => (
                <Card
                  key={i}
                  variant="outline"
                  className="px-5 py-4 flex flex-col gap-3 border-gray-20"
                >
                  <SkeletonLoader height={10} width={80} borderRadius={4} />
                  <SkeletonLoader height={28} width={60} borderRadius={4} />
                  <SkeletonLoader height={10} width={120} borderRadius={4} />
                  <SkeletonLoader height={8} width={240} borderRadius={4} />
                </Card>
              ))}
            </div>

            {/* Skeleton table */}
            <Card variant="outline" className="p-0 border-gray-20 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-10 flex gap-8">
                {[140, 80, 110, 100, 130, 90, 110].map((w, i) => (
                  <SkeletonLoader key={i} height={12} width={w} borderRadius={4} />
                ))}
              </div>
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="px-6 py-5 border-b border-gray-10 last:border-b-0 flex items-center gap-8"
                >
                  <div className="flex items-center gap-2 w-[140px] shrink-0">
                    <SkeletonLoader height={28} width={28} borderRadius={6} />
                    <SkeletonLoader height={13} width={90} borderRadius={4} />
                  </div>
                  {[80, 110, 100, 130, 90, 110].map((w, j) => (
                    <SkeletonLoader key={j} height={13} width={w} borderRadius={4} />
                  ))}
                </div>
              ))}
            </Card>
          </>
        )}

        {summary?.data && (
          <>
            <AggregateStatCards
              m={summary.data}
              accentClass={barClassForCategory(selectedCategory)}
              compact
            />

            <Text variant="h4" className="mb-3 text-gray-70">
              Integrations
            </Text>
            <Card variant="outline" className="p-0 border-gray-20">
              <MergeTable
                hover
                hoverColor="#EBF7FC"
                hasMarginBottom={false}
                hasMorePaddingOnFirstElement
                hasColumnTitlesClickable
                header={
                  <>
                    <SortTh col="name" sortCol={iSortCol} sortDir={iSortDir} onSort={iHandleSort}>
                      Integration
                    </SortTh>
                    <SortTh
                      col="total_fetch_blueprints"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      Total BPs
                    </SortTh>
                    <SortTh
                      col="blueprints_with_at_least_one_code_block"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      With ≥1 code block
                    </SortTh>
                    <SortTh
                      col="blueprints_fully_converted"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      Fully converted
                    </SortTh>
                    <SortTh
                      col="blueprints_with_code_block_and_no_model_writes"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      Code block, no writes
                    </SortTh>
                    <SortTh
                      col="code_block_step_ratio"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      Step ratio (all)
                    </SortTh>
                    <SortTh
                      col="code_block_step_ratio_supported_steps_only"
                      sortCol={iSortCol}
                      sortDir={iSortDir}
                      onSort={iHandleSort}
                    >
                      Step ratio (convertible)
                    </SortTh>
                  </>
                }
                content={
                  <>
                    {sortedIntegrations.map((integration) => (
                      <tr
                        key={integration.id}
                        onClick={() =>
                          history.push(
                            getCodeBlockProgressIntegrationPath(integration.id, selectedCategory)
                          )
                        }
                        className="cursor-pointer"
                      >
                        <td>
                          <div className="flex items-center gap-2">
                            <IntegrationAvatar
                              integrationName={integration.name}
                              squareImage={integration.image ?? undefined}
                            />
                            <Text variant="h6">{integration.name}</Text>
                          </div>
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {integration.total_fetch_blueprints}
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {integration.blueprints_with_at_least_one_code_block}
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {integration.blueprints_fully_converted}
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {integration.blueprints_with_code_block_and_no_model_writes}
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {(integration.code_block_step_ratio * 100).toFixed(1)}%
                        </td>
                        <td className="text-base tabular-nums text-gray-60">
                          {(integration.code_block_step_ratio_supported_steps_only * 100).toFixed(
                            1
                          )}
                          %
                        </td>
                      </tr>
                    ))}
                  </>
                }
              />
            </Card>
          </>
        )}
      </Container>
    );
  }

  // ── Category grid view ─────────────────────────────────────────────────────

  return (
    <Container fluid className="pb-8 px-8 pt-16">
      <div className="flex items-center gap-2 mt-10">
        <Text variant="h2" className="text-gray-70">
          Code block conversion progress
        </Text>
        <Tooltip
          title="% of integrations with ≥1 fully converted blueprint. A blueprint is fully converted if it has a code block and either no model-write steps or ≥15% of its convertible steps are code block steps."
          placement="right"
        >
          <Info size={18} className="text-gray-30 cursor-help shrink-0" />
        </Tooltip>
      </div>
      <div className="mt-3 mb-8 inline-flex items-center gap-2 rounded-md bg-blue-0 border border-blue-20 px-3 py-2 text-sm text-blue-60">
        <Info size={14} className="shrink-0" />
        Metrics cover fetch blueprints only. Click a category to see per-integration breakdown.
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-3 gap-6">
        {TRACKED_CATEGORIES.map((cat) => (
          <CategoryCard
            key={cat}
            category={cat}
            summary={summaries[cat] ?? { data: null, loading: true, error: false }}
            onClick={() => handleCategoryClick(cat)}
          />
        ))}
      </div>
    </Container>
  );
};

export default CodeBlockProgressPage;
